import omitBy from 'lodash/omitBy';
import getWindow from '@loggi/js/get-window';
import { getOptimizelyActiveExperiments } from './optimizely';
import getTrafficSourceProps from './traffic-source';
import { isBlackList, eventBoilerplate } from './utils';

let currentProduct;

function setAmplitude(Amplitude) {
  window.amplitude = Amplitude;
}

function getAmplitude() {
  const window = getWindow() || {};
  return window.amplitude || null;
}

function logEvent(event, properties = {}, callback) {
  const amplitude = getAmplitude();

  if (!amplitude) {
    return false;
  }

  amplitude.logEvent(
    event,
    {
      ...properties,
      product: currentProduct
    },
    callback
  );

  return true;
}

function setUserProperties(properties) {
  const amplitude = getAmplitude();

  if (!amplitude) {
    return false;
  }

  amplitude.setUserProperties(properties);
  return true;
}

function setDeviceId(id = +new Date()) {
  const amplitude = getAmplitude();

  if (!amplitude) {
    return false;
  }

  amplitude.setDeviceId(id);
  return true;
}

function setUserId(id) {
  const amplitude = getAmplitude();

  if (!amplitude) {
    return false;
  }

  amplitude.setUserId(id);
  return true;
}

function install(product, user = {}) {
  const amplitude = getAmplitude();

  if (!amplitude) {
    return false;
  }

  if (user !== null) {
    user.product = product;
    setUserProperties(user);
  }

  currentProduct = product;
  return true;
}

function middleware(actionMapper) {
  return store => next => action => {
    const eventName = actionMapper[action.type];

    if (eventName) {
      const log =
        typeof eventName === 'function'
          ? eventName(store.getState(), action)
          : { event: eventName };

      if (log !== null) {
        logEvent(log.event, log.properties || {});
      }
    }

    return next(action);
  };
}

function setInitialProperties() {
  const amplitude = getAmplitude();
  if (!amplitude) {
    return false;
  }

  const trafficSourceProps = getTrafficSourceProps() || {};

  // Set initial utm values
  const initialIdentify = new amplitude.Identify();
  Object.keys(trafficSourceProps).forEach(paramKey =>
    initialIdentify.setOnce(`initial_${paramKey}`, trafficSourceProps[paramKey])
  );
  amplitude.identify(initialIdentify);

  // Set optimizely active experiments as user props
  const optimizelyProps = {
    activeExperiments: getOptimizelyActiveExperiments().map(
      ({ name, variation }) => `${name}_${variation}`
    )
  };

  // Set user properties
  setUserProperties(Object.assign(trafficSourceProps, optimizelyProps));
  return true;
}

(() => setInitialProperties())();

function reduxAmplitudeEvent(amplitudeEvents, defaultProperties = {}) {
  return (store, action) => {
    /* eslint-disable no-use-before-define */
    const { type, ...properties } = action;
    const { payload = {}, ...rest } = properties;
    /* eslint-enable no-use-before-define */

    const ommitedPayload = omitBy(payload, isBlackList);
    return eventBoilerplate(amplitudeEvents[type], {
      ...rest,
      ...ommitedPayload,
      ...defaultProperties
    });
  };
}

export default {
  logEvent,
  install,
  setUserProperties,
  setInitialProperties,
  setDeviceId,
  middleware,
  setUserId,
  setAmplitude,
  reduxAmplitudeEvent
};
